import type { ComponentChild } from 'preact';
import { useLayoutEffect, useRef } from 'preact/hooks';
import { GRADE_WORDS, Limit, NightScore, SkyHour, seeingGrade } from '../live/skyforecast';
import { fmtTime } from './format';

// The weather that matters to a stargazer, hour by hour, and in plain words underneath.

const LIMIT_NOUN: Record<Limit, string> = {
  cloud: 'cloud',
  moon: 'the bright Moon',
  twilight: 'twilight',
  haze: 'hazy air',
  fog: 'the risk of fog',
  lights: 'light pollution',
};

/** "cloud holds it back", "light pollution and the bright Moon hold it back". */
export function limitText(limits: Limit[], neverDark = false): string {
  const words = limits.map((l) => (l === 'twilight' && neverDark ? 'the light summer night' : LIMIT_NOUN[l]));
  if (!words.length) return 'nothing much holding it back';
  return words.length === 1 ? `${words[0]} holds it back` : `${words.join(' and ')} hold it back`;
}

type Tone = 'g' | 'o' | 'p' | 'n';
const tone = (v: number | null | undefined, good: (x: number) => boolean, ok: (x: number) => boolean): Tone =>
  v == null ? 'n' : good(v) ? 'g' : ok(v) ? 'o' : 'p';
const median = (xs: number[]) => {
  const s = [...xs].sort((a, b) => a - b);
  return s.length ? s[Math.floor(s.length / 2)] : null;
};
const pct = (p: number) => `${Math.round(p * 10) * 10}%`;
/** "a 60%" but "an 80%". */
export const aPct = (p: number) => `${/^(8|11|18)/.test(String(Math.round(p * 10) * 10)) ? 'an' : 'a'} ${pct(p)}`;

/** The night's clear-sky outlook in one line. */
export function clearLine(dark: SkyHour[]): string {
  const pc = dark.filter((h) => h.pClear != null);
  if (!pc.length) return 'Forecast unavailable';
  const avg = pc.reduce((a, h) => a + h.pClear!, 0) / pc.length;
  const bestI = pc.reduce((b, h, i) => (h.pClear! > pc[b].pClear! ? i : b), 0);
  const best = pc[bestI].pClear!;
  if (avg >= 0.75) return 'Clear for most of the night';
  if (best < 0.15) return 'Cloudy: little chance of a clear sky tonight';
  // The run of hours around the best one that are nearly as good.
  let a = bestI, b = bestI;
  while (a > 0 && pc[a - 1].pClear! >= best - 0.1) a--;
  while (b < pc.length - 1 && pc[b + 1].pClear! >= best - 0.1) b++;
  const when = a === b ? `around ${fmtTime(pc[a].t)}` : `${fmtTime(pc[a].t)}–${fmtTime(pc[b].t + 3600e3)}`;
  return `Clearest ${when}, with about ${aPct(best)} chance of clear sky`;
}

function seeingLine(dark: SkyHour[]): string | null {
  const s = median(dark.flatMap((h) => (h.seeing != null ? [h.seeing] : [])));
  if (s == null) return null;
  const g = seeingGrade(s);
  const what = g <= 1 ? 'steady air for planets and the Moon' : g === 2 ? 'some shimmer at high magnification' : 'planets will look blurred';
  const jet = Math.max(0, ...dark.map((h) => h.jet ?? 0));
  const jetNote = jet > 35 ? ` A fast jet stream overhead (about ${Math.round(jet * 2.237)} mph) can make planets shimmer anyway.` : '';
  return `Seeing ${GRADE_WORDS[g]} overhead, about ${s.toFixed(1)}″: ${what}.${jetNote}`;
}

function transparencyLine(dark: SkyHour[]): string | null {
  const g = median(dark.flatMap((h) => (h.transparency != null ? [h.transparency] : [])));
  if (g == null) return null;
  const what = g <= 1 ? 'clean air, so faint stars and the Milky Way stand out' : g === 2 ? 'a little haze' : 'hazy air dims faint stars';
  const high = dark.some((h) => (h.high ?? 0) > 30 && (h.cloud ?? 100) < 70);
  return `Transparency ${GRADE_WORDS[g]}: ${what}${high ? '; thin high cloud at times' : ''}.`;
}

function dampLine(dark: SkyHour[]): string | null {
  const parts: string[] = [];
  const dew2 = dark.find((h) => h.dew === 2), dew1 = dark.find((h) => h.dew === 1);
  if (dew2) parts.push(`Dew likely from ${fmtTime(dew2.t)}: keep lenses capped, and use a dew heater if you have one.`);
  else if (dew1) parts.push(`Dew possible from ${fmtTime(dew1.t)}.`);
  const fog2 = dark.find((h) => h.fog === 2), fog1 = dark.find((h) => h.fog === 1);
  if (fog2) parts.push(`Fog likely from ${fmtTime(fog2.t)}.`);
  else if (fog1) parts.push(`Mist or fog possible from ${fmtTime(fog1.t)}.`);
  if (dark.some((h) => h.frost)) parts.push('Frost likely: wrap up warm.');
  return parts.length ? parts.join(' ') : null;
}

function tempLine(dark: SkyHour[]): string | null {
  const withT = dark.filter((h) => h.temp != null);
  if (!withT.length) return null;
  const first = withT[0];
  const cold = withT.reduce((m, h) => (h.temp! < m.temp! ? h : m), first);
  const feels = Math.min(...withT.map((h) => h.feels ?? h.temp!));
  const wind = withT.reduce((a, h) => a + (h.wind ?? 0), 0) / withT.length;
  const windWords = wind < 3 ? 'light wind' : wind < 6 ? 'a breeze' : 'windy';
  let s = `${Math.round(first.temp!)} °C at ${fmtTime(first.t)}`;
  if (cold !== first && cold.temp! < first.temp! - 0.5) s += `, falling to ${Math.round(cold.temp!)} °C by ${fmtTime(cold.t)}`;
  if (feels < cold.temp! - 2) s += `, feeling like ${Math.round(feels)} °C`;
  return `${s}; ${windWords}.`;
}

export function SkyConditions({ hours, score, from, to, darkFrom, darkTo, now, runs, model }: {
  hours: SkyHour[];
  /** Ensemble runs behind the clear-sky chance (0 if they failed to load), and the model behind the cloud row. */
  runs: number;
  model?: string;
  score: NightScore;
  from: number;
  to: number;
  darkFrom: number;
  darkTo: number;
  now: number;
}) {
  const cols = hours.filter((h) => h.t >= from - 1800e3 && h.t <= to);
  const dark = hours.filter((h) => h.t >= darkFrom - 1800e3 && h.t <= darkTo);
  const byT = new Map(score.hours.map((s) => [s.t, s]));
  const best = score.best;
  const inBest = (t: number) => !!best && t >= best.start && t < best.end;
  const isNow = (t: number) => now >= t && now < t + 3600e3;
  const head = (t: number) => `${inBest(t) ? 'best ' : ''}${isNow(t) ? 'now' : ''}`;
  const lines = [seeingLine(dark), transparencyLine(dark), dampLine(dark), tempLine(dark)].filter(Boolean) as string[];
  // Fade the right edge while more hours lie off to the side.
  const wrap = useRef<HTMLDivElement>(null);
  const fade = () => {
    const w = wrap.current;
    if (w) w.classList.toggle('more', w.scrollLeft + w.clientWidth < w.scrollWidth - 4);
  };
  useLayoutEffect(fade);
  const rows: { label: string; full: string; cell: (h: SkyHour) => { tone: Tone; text: ComponentChild; cls?: string; aria?: string } }[] = [
    { label: 'Score', full: 'Stargazing score out of 10', cell: (h) => { const s = byT.get(h.t)?.score; return { tone: tone(s, (x) => x >= 6.5, (x) => x >= 3.5), text: s != null ? Math.round(s) : '' }; } },
    { label: 'Clear %', full: runs ? `Chance of a clear sky, from ${runs} forecast runs, per cent` : 'Chance of a clear sky, from the main forecast alone, per cent', cell: (h) => ({ tone: tone(h.pClear, (x) => x >= 0.7, (x) => x >= 0.4), text: h.pClear != null ? Math.round(h.pClear * 10) * 10 : '' }) },
    { label: 'Cloud %', full: `Cloud cover in the ${model ?? 'main'} forecast, per cent`, cell: (h) => ({ tone: tone(h.cloud, (x) => x <= 20, (x) => x <= 60), text: h.cloud != null ? Math.round(h.cloud) : '' }) },
    { label: 'Seeing ″', full: 'Seeing overhead in arcseconds: smaller is steadier', cell: (h) => ({ tone: tone(h.seeing != null ? seeingGrade(h.seeing) : null, (x) => x <= 1, (x) => x <= 2), text: h.seeing != null ? h.seeing.toFixed(1) : '' }) },
    {
      label: 'Clarity', full: 'Transparency: how clean the air is; taller is better',
      cell: (h) => ({
        tone: tone(h.transparency, (x) => x <= 1, (x) => x <= 2),
        text: h.transparency != null ? <span class="cg-bar" style={{ height: `${3 + (4 - h.transparency) * 3}px` }} /> : '',
        cls: 'cg-clarity',
        aria: h.transparency != null ? GRADE_WORDS[h.transparency] : undefined,
      }),
    },
    { label: '°C', full: 'Temperature, °C', cell: (h) => ({ tone: 'n', text: h.temp != null ? Math.round(h.temp) : '', cls: h.frost ? 'frost' : '' }) },
  ];
  return (
    <>
      <div class="card-main">
        {cols.length || !hours.length
          ? clearLine(dark.length ? dark : cols)
          : from > hours[hours.length - 1].t
            ? 'No forecast this far ahead yet: it reaches about three days'
            : 'No forecast kept for past nights'}
      </div>
      {cols.length > 0 && (
        <div class="cgrid-box">
          {/* Row names stay put while the hours scroll; the table keeps its own names for screen readers. */}
          <div class="cg-labels" aria-hidden="true">
            <span class="cg-corner" />
            {rows.map((r) => <span key={r.label} title={r.full}>{r.label}</span>)}
          </div>
          <div class="cgrid-wrap" ref={wrap} onScroll={fade}>
            <table class="cgrid tnum">
              <caption class="sr-only">Hour-by-hour conditions tonight</caption>
              <thead>
                <tr>
                  <td class="sr-only" />
                  {cols.map((h) => <th scope="col" key={h.t} class={head(h.t)}>{fmtTime(h.t).slice(0, 2)}</th>)}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.label}>
                    <th scope="row" class="sr-only">{r.full}</th>
                    {cols.map((h) => {
                      const c = r.cell(h);
                      return <td key={h.t} class={`t-${c.tone} ${c.cls ?? ''} ${head(h.t)}`} aria-label={c.aria}>{c.text}</td>;
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {lines.map((l) => <div class="card-sub cond-line" key={l}>{l}</div>)}
    </>
  );
}
